import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Search, Github } from "lucide-react";
import { PORTFOLIO } from "../data/portfolio";
import PortfolioPageShell from "../components/portfolio/PortfolioPageShell";

const CATEGORY_COLORS = {
  "Open Source": "#15803D",
  "Product R&D": "#BE185D",
  "Design Systems": "#B45309",
};

const FILTERS = ["All", "Open Source", "Product R&D", "Design Systems"];
const CONTROL_SHADOW = "5px 5px 0 0 #FBBF24";
const CARD_SHADOW = "5px 5px 0 0 #15803D";

function ActionButton({ links }) {
  if (!links) return null;
  const primary = links.docs
    ? { href: links.docs, label: "DOCUMENTATION", icon: <ExternalLink size={14} />, external: true }
    : links.pypi
    ? { href: links.pypi, label: "VIEW ON PYPI", icon: <ExternalLink size={14} />, external: true }
    : links.github
    ? { href: links.github, label: "VIEW ON GITHUB", icon: <Github size={14} />, external: true }
    : links.blogs
    ? { href: links.blogs, label: "READ BLOGS", icon: <ArrowRight size={14} />, external: false }
    : links.cookbooks
    ? { href: links.cookbooks, label: "OPEN COOKBOOKS", icon: <ArrowRight size={14} />, external: false }
    : links.site
    ? { href: links.site, label: "OPEN", icon: <ArrowRight size={14} />, external: false }
    : null;
  if (!primary) return null;
  const className =
    "inline-flex items-center gap-2 bg-[#4ADE80] text-black font-arcade text-base uppercase px-5 py-2.5 border-2 border-[#0A0A0A] hover:bg-[#15803D] hover:text-white transition-colors duration-150 btn-press w-fit";
  if (primary.external) {
    return (
      <a href={primary.href} target="_blank" rel="noopener noreferrer" className={className}>
        {primary.label} {primary.icon}
      </a>
    );
  }
  return (
    <Link to={primary.href} className={className}>
      {primary.label} {primary.icon}
    </Link>
  );
}

export default function PortfolioProjects() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PORTFOLIO.filter((p) => {
      if (filter !== "All" && p.category !== filter) return false;
      if (q === "") return true;
      const haystack = [
        p.title,
        p.subtitle,
        p.summary,
        p.description,
        ...(p.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [filter, search]);

  return (
    <PortfolioPageShell tag="PROJECTS" title="WHAT I'VE BUILT" maxWidth="max-w-7xl">
      <div
        data-testid="portfolio-controls"
        className="bg-white border-[3px] border-[#0A0A0A] p-4 mb-8"
        style={{ boxShadow: CONTROL_SHADOW }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-center">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={`font-arcade text-base uppercase tracking-wide px-4 py-2 border-2 transition-colors duration-150 ${
                  filter === f
                    ? "bg-[#FBBF24] text-black border-[#FBBF24]"
                    : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#FBBF24]/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40 pointer-events-none" />
            <input
              type="text"
              placeholder="search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="portfolio-search"
              className="w-full font-terminal text-sm border-2 border-[#0A0A0A] bg-[#F5F5F0] pl-9 pr-3 py-2 placeholder:text-[#0A0A0A]/40 focus:outline-none focus:bg-white"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filtered.map((item) => (
          <article
            key={item.id}
            id={item.id}
            data-testid={`portfolio-${item.id}`}
            className="group bg-white border-[3px] border-[#0A0A0A] p-4 flex flex-col scroll-mt-24 transition-transform duration-150 hover:-translate-y-1"
            style={{ boxShadow: CARD_SHADOW }}
          >
            <div className="relative h-48 mb-4 border-2 border-[#0A0A0A] bg-[#F5F5F0] overflow-hidden">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover [image-rendering:pixelated] transition-transform duration-300 group-hover:scale-[1.04]"
              />
            </div>
            <div className="flex items-center justify-between mb-3">
              <span
                className="font-arcade text-xs uppercase border px-2 py-0.5 bg-white"
                style={{
                  color: CATEGORY_COLORS[item.category] || "#15803D",
                  borderColor: CATEGORY_COLORS[item.category] || "#15803D",
                }}
              >
                {item.category}
              </span>
              <span className="font-terminal text-xs text-[#0A0A0A]/55">{item.year}</span>
            </div>
            <h3 className="font-pixel text-sm text-[#0A0A0A] leading-snug mb-1">{item.title}</h3>
            <p className="font-arcade text-base text-[#15803D] mb-3 leading-snug">{item.subtitle}</p>
            <p className="font-terminal text-xs text-[#0A0A0A]/55 leading-relaxed mb-4 flex-1">
              {item.summary}
            </p>
            {item.outcomes?.length > 0 && (
              <ul className="space-y-1.5 mb-4">
                {item.outcomes.slice(0, 3).map((o, oi) => (
                  <li key={oi} className="font-terminal text-xs text-[#0A0A0A]/70 flex items-start gap-2">
                    <span className="text-[#15803D] mt-0.5">&gt;</span> {o}
                  </li>
                ))}
              </ul>
            )}
            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {item.tags.slice(0, 5).map((t) => (
                  <span
                    key={t}
                    className="font-terminal text-xs text-[#0A0A0A] border border-[#0A0A0A] bg-[#F5F5F0] px-2 py-0.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-auto">
              <ActionButton links={item.links} />
              {item.links?.github && item.links?.pypi && (
                <a
                  href={item.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-transparent text-[#0A0A0A] font-arcade text-base uppercase px-4 py-2.5 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors duration-150 btn-press"
                >
                  <Github size={14} /> SRC
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="font-arcade text-xl text-[#0A0A0A]/30 text-center py-20">
          No projects match.
        </p>
      )}
    </PortfolioPageShell>
  );
}
